import Job from "../../apiResources/Job";
import Application from "../../apiResources/Application";
import ApplicationAction from "../../apiResources/ApplicationAction";
import JobHiringProcessTimeline from "../../apiResources/JobHiringProcessTimeline";
import Placement from "../../apiResources/Placement";
import YearlyHhTimeToHire from "../../apiResources/YearlyHhTimeToHire";
import JobPerformance from "../../apiResources/JobPerformance";
import PslPerformance from "../../apiResources/PslPerformance";
import applicationActionTrackingData, {
  IApplicationTrackingData,
} from "../adapters/employerAnalyticsAdapters/applicationActionTrackingAdapter";
import moment from "moment";

const epochStart: DateString = "1970-01-01";
const now: DateString = new Date().toISOString();
const today = new Date();
const oneDayInMS = 24 * 60 * 60 * 1000;

const sevenDaysAgo: DateString = new Date(Number(today) - 7 * oneDayInMS)
  .toISOString()
  .substr(0, 10);
const thirtyDaysAgo: DateString = new Date(Number(today) - 30 * oneDayInMS)
  .toISOString()
  .substr(0, 10);
const ninetyDaysAgo: DateString = new Date(Number(today) - 90 * oneDayInMS)
  .toISOString()
  .substr(0, 10);
const oneYearAgo: DateString = new Date(Number(today) - 365 * oneDayInMS)
  .toISOString()
  .substr(0, 10);

type DateString = string;
type Sector = string;

const calculateDateRange = (timeframe, customStartDate, customEndDate) => {
  // const today = new Date();
  let from = "",
    to = "";
  switch (timeframe) {
    case "Current Quarter":
      from = moment().startOf("quarter").format("YYYY-MM-DD HH:mm:ss");
      to = moment().endOf("quarter").format("YYYY-MM-DD HH:mm:ss");
      break;
    case "This Month":
      from = moment().startOf("month").format("YYYY-MM-DD HH:mm:ss");
      to = moment().endOf("month").format("YYYY-MM-DD HH:mm:ss");
      break;
    case "Last Month":
      from = moment()
        .subtract(1, "month")
        .startOf("month")
        .format("YYYY-MM-DD HH:mm:ss");
      to = moment()
        .subtract(1, "month")
        .endOf("month")
        .format("YYYY-MM-DD HH:mm:ss");
      break;
    case "Last Quarter":
      from = moment()
        .subtract(1, "quarter")
        .startOf("quarter")
        .format("YYYY-MM-DD HH:mm:ss");
      to = moment()
        .subtract(1, "quarter")
        .endOf("quarter")
        .format("YYYY-MM-DD HH:mm:ss");
      break;
    case "This Year":
      from = moment().startOf("year").format("YYYY-MM-DD HH:mm:ss");
      to = moment().endOf("year").format("YYYY-MM-DD HH:mm:ss");
      break;
    case "Last 12 Months":
      from = moment()
        .subtract(12, "months")
        .startOf("day")
        .format("YYYY-MM-DD HH:mm:ss");
      to = moment().endOf("day").format("YYYY-MM-DD HH:mm:ss");
      break;
    case "Last Year":
      from = moment()
        .subtract(1, "year")
        .startOf("year")
        .format("YYYY-MM-DD HH:mm:ss");
      to = moment()
        .subtract(1, "year")
        .endOf("year")
        .format("YYYY-MM-DD HH:mm:ss");
      break;
    case "Custom Date":
      from = moment(customStartDate, "DD/MM/YYYY")
        .startOf("day")
        .format("YYYY-MM-DD HH:mm:ss");
      to = moment(customEndDate, "DD/MM/YYYY")
        .endOf("day")
        .format("YYYY-MM-DD HH:mm:ss");
      break;
    default:
      // Handle default case or empty selection
      from = "";
      to = "";
      break;
  }

  return { from, to };
};

export const getEmployerJobStats = async (
  from: DateString = epochStart,
  to: DateString = now,
  employerId: number | null = null,
  currencyId: number | null = null
) =>
  Job.where({
    created_at: { gte: from, lte: to },
    employer_id: employerId,
    currency_id: currencyId,
  })
    .per(0)
    .stats({
      total_number_of_positions: "custom_stat",
      spend_per_sector: "custom_stat",
      positions_per_sector: "custom_stat",
      time_to_hire_per_month: "custom_stat",
    })
    .all();

export const getCandidateSubmission = async (getFilters) => {
  const { jobs, job_sectors, employer_users, customStartDate, customEndDate } =
    getFilters;
  const timeframe =
    getFilters?.timeframe && getFilters.timeframe.length > 0
      ? getFilters.timeframe
      : getFilters?.defaultFilters?.timeframe || [];

  let filteredTimeFrame;

  if (timeframe && timeframe.length > 0) {
    filteredTimeFrame = calculateDateRange(
      timeframe[0],
      customStartDate,
      customEndDate
    );
  } else {
    filteredTimeFrame = {};
  }
  const data = {
    start_date: filteredTimeFrame?.from,
    end_date: filteredTimeFrame?.to,
    ...(job_sectors && job_sectors.length != 0 && { sector_ids: job_sectors }),
    ...(employer_users &&
      employer_users.length != 0 && { employer_user_ids: employer_users }),
    ...(jobs && jobs.length != 0 && { job_ids: jobs }),
  };

  const jsonData = JSON.stringify(data);

  const jobPerformances = await JobPerformance.where({
    filters: jsonData,
  }).all();

  return jobPerformances;
};

export const getPslData = async (args, pagination) => {
  const jobSectors = args?.pslSectors;
  const agencies = args?.agencies;
  const selectedPagination = args?.pagination || pagination;
  const timeframe =
    args?.timeframe && args.timeframe.length > 0
      ? args.timeframe
      : args?.defaultFilters?.timeframe || [];
  const customStartDate = args?.customStartDate;
  const customEndDate = args?.customEndDate;
  const sortOrder =
    args?.sortOrder ||
    (args && args.defaultFilters && args.defaultFilters.sortOrder) ||
    "desc";
  const sortBy =
    args?.sortBy ||
    (args && args.defaultFilters && args.defaultFilters.sortBy) ||
    "jobs_total_filled";

  let filteredTimeFrame;
  const sortable = { [sortBy]: sortOrder };

  if (timeframe && timeframe.length > 0) {
    filteredTimeFrame = calculateDateRange(
      timeframe[0],
      customStartDate,
      customEndDate
    );
  } else {
    filteredTimeFrame = {};
  }
  const page = selectedPagination?.page || 1;

  const whereConditions = {
    sectors: jobSectors,
    id: agencies,
    start_date: filteredTimeFrame.from,
    end_date: filteredTimeFrame.to,
  };

  const pslPerformances =
    args && Object.entries(args).length > 0
      ? await PslPerformance.page(page)
          .per(10)
          .where(whereConditions)
          .order(sortable)
          .stats({ total: "count" })
          .all()
      : await PslPerformance.select(["agency_name,sectors"]).all();

  return pslPerformances;
};

export const getEmployerJobCount = async (
  from: DateString = epochStart,
  to: DateString = now
) =>
  Job.where({
    created_at: { gte: from, lte: to },
  })
    .per(0)
    .stats({ total_number_of_positions: "custom_stat" })
    .all();

export const getEmployerSpendPerSector = async (
  from: DateString = epochStart,
  to: DateString = now
) =>
  Job.where({
    application_accepted_at_range: { gte: from, lte: to },
  })
    .per(0)
    .stats({ spend_per_sector: "custom_stat" })
    .all();

export const getEmployerJobsPerSector = async (
  from: DateString = epochStart,
  to: DateString = now
) =>
  Job.where({
    created_at: { gte: from, lte: to },
  })
    .per(0)
    .stats({ positions_per_sector: "custom_stat" })
    .all();

export const getEmployerAvgTimeToHireLastYear = async (
  from: DateString = epochStart,
  to: DateString = now,
  employerId: number | null = null
) =>
  Job.where({
    created_at: { gte: from, lte: to },
    employer_id: employerId,
  })
    .per(0)
    .stats({ time_to_hire_last_year: "custom_stat" })
    .all();

export const getEmployerTimeToHirePerMonth = async (
  from: DateString = epochStart,
  to: DateString = now
) =>
  Job.where({
    created_at: { gte: from, lte: to },
  })
    .per(0)
    .stats({ time_to_hire_per_month: "custom_stat" })
    .all();

export const getEmployerTimeToHireLastNinetyDays = async (
  from: DateString = epochStart,
  to: DateString = now
) =>
  Job.where({
    created_at: { gte: from, lte: to },
  })
    .per(0)
    .stats({ time_to_hire_per_week_last_three_months: "custom_stat" })
    .all();

export const getEmployerTimeToHireLastThirtyDays = async (
  from: DateString = epochStart,
  to: DateString = now
) =>
  Job.where({
    created_at: { gte: from, lte: to },
  })
    .per(0)
    .stats({ time_to_hire_per_week_last_month: "custom_stat" })
    .all();

export const getEmployerApplicationCount = async (
  from: DateString = epochStart,
  to: DateString = now,
  employerId: number | null = null
) =>
  Application.where({
    created_at: { gte: from, lte: to },
    include_deleted: true,
    employer_id: employerId,
  })
    .per(0)
    .stats({ total: "count" })
    .all();

export const getEmployerInterviewCount = async (
  from: DateString = epochStart,
  to: DateString = now,
  employerId: number | null = null
) =>
  ApplicationAction.where({
    created_at: { gte: from, lte: to },
    action: [
      "interview_requested",
      "telephone_interview_requested",
      "first_interview_requested",
      "second_interview_requested",
    ],
    employer_id: employerId,
  })
    .per(0)
    .stats({ total: "count" })
    .all();

export const getOfferCounts = async (
  from: DateString = epochStart,
  to: DateString = now,
  employerId: number | null = null
) =>
  ApplicationAction.where({
    created_at: { gte: from, lte: to },
    action: "offered_job",
    employer_id: employerId,
  })
    .per(0)
    .stats({
      total: "count",
      hiring_hub_offers_past_12_months: "custom_stat",
    })
    .all();

export const getEmployerOfferCount = async (
  from: DateString = epochStart,
  to: DateString = now
) =>
  ApplicationAction.where({
    created_at: { gte: from, lte: to },
    action: "offered_job",
  })
    .per(0)
    .stats({ total: "count" })
    .all();

export const getHiringHubOfferCount = async (
  from: DateString = epochStart,
  to: DateString = now
) =>
  ApplicationAction.where({
    created_at: { gte: from, lte: to },
  })
    .per(0)
    .stats({ hiring_hub_offers_past_12_months: "custom_stat" })
    .all();

export const getPlacementCounts = async (
  from: DateString = epochStart,
  to: DateString = now,
  employerId: number | null = null
) =>
  Placement.where({
    status: "successful",
    created_at: { gte: from, lte: to },
    employer_id: employerId,
  })
    .per(0)
    .stats({
      total: "count",
      hiring_hub_placements_past_12_months: "custom_stat",
    })
    .all();

export const getEmployerPlacementCount = async (
  from: DateString = epochStart,
  to: DateString = now
) =>
  Placement.where({ status: "successful", created_at: { gte: from, lte: to } })
    .per(0)
    .stats({ total: "count" })
    .all();

export const getHiringHubPlacementCount = async (
  from: DateString = epochStart,
  to: DateString = now
) =>
  Placement.where({
    created_at: { gte: from, lte: to },
  })
    .per(0)
    .stats({ hiring_hub_placements_past_12_months: "custom_stat" })
    .all();

type Timescale = number;

// timescale filter helpers
const days = (timescale: Timescale): DateString => {
  if (timescale === 7) {
    return sevenDaysAgo;
  }
  if (timescale === 30) {
    return thirtyDaysAgo;
  }
  if (timescale === 90) {
    return ninetyDaysAgo;
  }
  return epochStart;
};

export const selectPipelineTimescale = async (timescale: Timescale) => {
  const jobCount = await getEmployerJobCount(days(timescale), now);

  const applicationsCount = await getEmployerApplicationCount(
    days(timescale),
    now
  );
  const interviewsCount = await getEmployerInterviewCount(days(timescale), now);
  const offersCount = await getEmployerOfferCount(days(timescale), now);
  const placementsCount = await getEmployerPlacementCount(days(timescale), now);

  /* eslint-disable @typescript-eslint/no-unsafe-member-access */
  return {
    positions: jobCount.meta.stats.total_number_of_positions
      .custom_stat as Record<Sector, number>,
    applications: applicationsCount.meta.stats.total.count as number,
    interviews: interviewsCount.meta.stats.total.count as number,
    offers: offersCount.meta.stats.total.count as number,
    placements: placementsCount.meta.stats.total.count as number,
  };
  /* eslint-enable @typescript-eslint/no-unsafe-member-access */
};

interface ISpendPerSector {
  sectors: Array<Sector>;
  values: Array<number>;
}

export const selectSpendPerSectorTimescale = async (
  timescale: Timescale
): Promise<ISpendPerSector> => {
  const employerSpendPerSector = await getEmployerSpendPerSector(
    days(timescale),
    now
  );

  /* eslint-disable-next-line @typescript-eslint/no-unsafe-member-access */
  const employerSpendPerSectorCount = employerSpendPerSector.meta.stats
    .spend_per_sector.custom_stat as Record<Sector, number>;

  return {
    sectors: Object.keys(employerSpendPerSectorCount),
    values: Object.values(employerSpendPerSectorCount),
  };
};

export const selectJobsPerSectorTimescale = async (timescale: Timescale) => {
  const employerJobsPerSector = await getEmployerJobsPerSector(
    days(timescale),
    now
  );

  /* eslint-disable @typescript-eslint/no-unsafe-member-access */
  const employerJobsPerSectorCount = employerJobsPerSector.meta.stats
    .positions_per_sector.custom_stat as Record<string, number>;

  return {
    sectors: Object.keys(employerJobsPerSectorCount),
    values: Object.values(employerJobsPerSectorCount),
  };
};

export const selectTthTimelineTimescale = async (timescale: Timescale) => {
  let timeToHireMonths: Array<DateString>;
  let employerTimeToHire: Array<number>;
  let hiringHubTimeToHire: Array<number>;
  let industryTimeToHire: Array<number>;

  const hhTimeToHire = await YearlyHhTimeToHire.first();
  const hhTimeToHireData = hhTimeToHire.data;

  if (timescale === 30) {
    const employerTimeToHireLastThirtyDays =
      await getEmployerTimeToHireLastThirtyDays();

    timeToHireMonths = Object.keys(hhTimeToHireData.lastThirtyDaysWeekly);
    const employerTimeToHireLastThirtyDaysMeta =
      employerTimeToHireLastThirtyDays.meta;
    employerTimeToHire = Object.values(
      employerTimeToHireLastThirtyDaysMeta.stats
        .time_to_hire_per_week_last_month.custom_stat
    );
    hiringHubTimeToHire = Object.values(hhTimeToHireData.lastThirtyDaysWeekly);
    industryTimeToHire = [36, 36, 36, 36, 36];
  } else if (timescale === 90) {
    const employerTimeToHireLastNinetyDays =
      await getEmployerTimeToHireLastNinetyDays();
    timeToHireMonths = Object.keys(hhTimeToHireData.lastNinetyDaysWeekly);
    const employerTimeToHireLastNinetyDaysMeta =
      employerTimeToHireLastNinetyDays.meta;
    employerTimeToHire = Object.values(
      employerTimeToHireLastNinetyDaysMeta.stats
        .time_to_hire_per_week_last_three_months.custom_stat
    );
    hiringHubTimeToHire = Object.values(hhTimeToHireData.lastNinetyDaysWeekly);
    industryTimeToHire = [36, 36, 36, 36, 36, 36, 36, 36, 36, 36, 36, 36, 36];
  } else {
    const employerTimeToHirePerMonth = await getEmployerTimeToHirePerMonth();
    timeToHireMonths = Object.keys(hhTimeToHireData.lastTwelveMonthsMonthly);
    employerTimeToHire = Object.values(
      employerTimeToHirePerMonth.meta.stats.time_to_hire_per_month.custom_stat
    );
    hiringHubTimeToHire = Object.values(
      hhTimeToHireData.lastTwelveMonthsMonthly
    );
    industryTimeToHire = [36, 36, 36, 36, 36, 36, 36, 36, 36, 36, 36, 36];
  }

  return {
    months: timeToHireMonths,
    yourCompany: employerTimeToHire,
    hiringHub: hiringHubTimeToHire,
    industryAvg: industryTimeToHire,
  };
};

interface IJobHiringProcessTimeline {
  daysToFirstApplication: number;
  daysFromApplicationToFirstInterview: number;
  daysFromInterviewToFirstOffer: number;
  daysFromOfferToFirstOfferAccepted: number;
}

interface IJobHiringProcessTimelineCollection {
  data: Array<IJobHiringProcessTimeline>;
}

export const selectApplicationActionsTrackingTimescale = async (
  timescale: number
): Promise<IApplicationTrackingData> => {
  let timelines: IJobHiringProcessTimelineCollection;

  if (timescale === 0) {
    timelines = await JobHiringProcessTimeline.where({
      job_posted_date: { gte: oneYearAgo },
    }).all();
  } else {
    timelines = await JobHiringProcessTimeline.where({
      job_posted_date: { gte: days(timescale) },
    }).all();
  }

  return applicationActionTrackingData(timelines.data);
};
